// src/services/api.ts

import axios, { AxiosError } from 'axios';
import { HotelInfo, Metric, AnalysisResponse } from '@types'; // Assuming types are correctly defined
import {
  API_CONSTANTS,
  retryWithBackoff,
  extractErrorMessage,
  formatArrayForPostgres,
  cleanMetricValue
} from '@utils';
import { API_CONFIG } from '../config/api';

// Get API base URL from centralized config
const API_HOST = API_CONFIG.BASE_URL;
// API path prefix - will be added to all requests
const API_PREFIX = '/api';

// Debug information for API configuration
console.log('API Base URL:', API_HOST);
console.log('Environment:', (process.env as any).NODE_ENV);
console.log('Using API at:', `${API_HOST}${API_PREFIX}`);

// Constants
const TOKEN_KEY = API_CONSTANTS.TOKEN_KEY; // Use centralized token key

// Determine if we're running locally
const isLocalDevelopment = API_HOST.includes('localhost');

// Token manager singleton for keeping track of authentication state
const tokenManager = {
  getToken: () => {
    try {
      return localStorage.getItem('token');
    } catch (error) {
      console.error('Error accessing localStorage:', error);
      return null;
    }
  },
  setToken: (token: string) => {
    try {
      localStorage.setItem('token', token);
    } catch (error) {
      console.error('Error saving token to localStorage:', error);
    }
  },
  removeToken: () => {
    try {
      localStorage.removeItem('token');
    } catch (error) {
      console.error('Error removing token from localStorage:', error);
    }
  }
};

// Error handling utility
const handleApiError = (error: unknown) => {
  console.error('API Error:', error);

  // Check if this is an axios error with a response
  if (axios.isAxiosError(error) && error.response) {
    const { status, data } = error.response;

    // Handle different status codes
    if (status === 401 || status === 403) {
      // Authentication error - clear token and redirect to login
      tokenManager.removeToken();
      if (typeof window !== 'undefined') {
        window.location.href = '/login';
      }
    }

    // Return standardized error object
    return {
      status,
      message: data?.error || 'Unknown API error',
      details: data?.details || null
    };
  }

  // For non-axios errors
  return {
    status: 500,
    message: error instanceof Error ? error.message : 'Unknown error',
    details: null
  };
};

// Create axios instance
const axiosInstance = axios.create({
  baseURL: API_HOST + API_PREFIX,
  timeout: 300000, // 5 minute timeout
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
  // Always send credentials for all environments
  withCredentials: true,
});

// --- Request Interceptor ---
axiosInstance.interceptors.request.use(
  async (config) => {
    // Check and fix duplicate API prefixes in URL
    if (config.url && config.url.startsWith('/api/') && config.baseURL && config.baseURL.endsWith('/api')) {
      console.log(`Preventing duplicate API prefix in request URL: ${config.url}`);
      // Remove the /api prefix from the URL as it's already in the baseURL
      config.url = config.url.substring(4);
    }

    // Try to get token using the token manager
    const token = tokenManager.getToken();

    // Check if we're requesting an endpoint that doesn't require auth
    const publicEndpoints = ['/auth/login', '/auth/register', '/auth/forgot-password'];
    const isPublicEndpoint = publicEndpoints.some(endpoint =>
      config.url?.includes(endpoint)
    );

    // Create a simple flag to prevent auth loops with metrics
    const isMetricsEndpoint = config.url?.includes('/metrics') || false;
    const skipAuth = config.headers?.['X-Skip-Auth'] === 'true';

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      // Add detailed token debugging in development
      if ((process.env as any).NODE_ENV === 'development') {
        console.log(`API Auth Debug: Token found via tokenManager (length: ${token.length})`);
        try {
          // Simple JWT parts check - doesn't validate signature
          const [header, payload, signature] = token.split('.');
          if (header && payload && signature) {
            const decoded = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
            console.log(`API Auth Debug: Token payload:`, {
              exp: decoded.exp ? new Date(decoded.exp * 1000).toISOString() : 'missing',
              iat: decoded.iat ? new Date(decoded.iat * 1000).toISOString() : 'missing',
              expiresIn: decoded.exp ? Math.floor((decoded.exp * 1000 - Date.now()) / 1000) + 's' : 'unknown',
              userId: decoded.userId || decoded.sub || 'missing',
            });
          } else {
            console.warn('API Auth Debug: Token doesn\'t appear to be valid JWT format');
          }
        } catch (e) {
          console.warn('API Auth Debug: Error decoding token', e);
        }
      }
    } else if (!isPublicEndpoint) {
      console.warn('API Auth Debug: No token found via tokenManager for protected endpoint');

      // Handle metrics endpoints specially to prevent auth loops
      if (isMetricsEndpoint) {
        if (skipAuth) {
          console.log('Skipping auth for metrics endpoint to prevent loop');
          delete config.headers.Authorization;
        } else {
          // Add a header so we know not to try auth again
          config.headers['X-Skip-Auth'] = 'true';
        }
      }
    }

    // Log request in development
    if ((process.env as any).NODE_ENV === 'development') {
      console.log(`API Request: ${config.method?.toUpperCase()} ${config.url}`, {
        hasAuthHeader: !!config.headers.Authorization,
        baseURL: config.baseURL,
        skipAuth: skipAuth
      });
    }
    return config;
  },
  (error) => {
    console.error('Request Interceptor Error:', error);
    return Promise.reject(error);
  },
);

// --- Response Interceptor ---
axiosInstance.interceptors.response.use(
  (response) => {
    // For successful responses, just return the response
    return response;
  },
  (error) => {
    // Check if this is an auth error for metrics endpoints
    if (axios.isAxiosError(error) &&
        (error.response?.status === 401 || error.response?.status === 403) &&
        error.config?.url &&
        error.config.url.includes('/metrics')) {

      console.log('Handling auth error for metrics endpoint:', error.config.url);

      // For metrics endpoints, return empty data instead of error
      return { data: [] };
    }

    // Check for duplicate /api prefix in the URL - this causes 404 errors
    if (axios.isAxiosError(error) &&
        error.response?.status === 404 &&
        error.config?.url &&
        error.config.url.includes('/api/api/')) {

      console.log('Fixing duplicate API prefix in URL:', error.config.url);
      const originalRequest = error.config;
      // Fix the URL by replacing /api/api/ with /api/
      originalRequest.url = originalRequest.url?.replace('/api/api/', '/api/');
      return axiosInstance(originalRequest);
    }

    // Handle 404 errors with retry at different URL format
    if (axios.isAxiosError(error) && error.response?.status === 404 && error.config) {
      const originalRequest = error.config;
      const url = originalRequest.url || '';

      // Only retry for certain paths
      const retryablePaths = ['/metrics', '/documents', '/financials', '/monthly-metrics'];
      const shouldRetry = retryablePaths.some(path => url.includes(path));

      if (shouldRetry && !('_isRetry' in originalRequest) && originalRequest.url) {
        console.log(`Retry with alternative URL format for: ${url}`);
        // Add custom property to track retry
        const modifiedRequest = { ...originalRequest, _isRetry: true };

        // If URL starts with /api/, try without it, and vice versa
        if (url.startsWith('/api/')) {
          modifiedRequest.url = url.substring(4); // Remove /api prefix
          return axiosInstance(modifiedRequest);
        } else if (!url.startsWith('/api/')) {
          modifiedRequest.url = `/api${url}`; // Add /api prefix
          return axiosInstance(modifiedRequest);
        }
      }
    }

    // For other errors, reject the promise
    return Promise.reject(error);
  }
);

// Define API methods
const api = {
  // Auth methods
  login: async (email: string, password: string) => {
    try {
      const response = await axiosInstance.post('/auth/login', { email, password });
      const { token, user } = response.data;
      tokenManager.setToken(token);
      return { success: true, user };
    } catch (error) {
      return { success: false, error: handleApiError(error) };
    }
  },

  logout: () => {
    tokenManager.removeToken();
    return { success: true };
  },

  // Basic HTTP methods
  get: async <T = any>(url: string, config?: any) => {
    // Add retry logic for network errors
    const maxRetries = 2;
    let retryCount = 0;

    const executeRequest = async (): Promise<{ data: T }> => {
      try {
        return await axiosInstance.get(url, config);
      } catch (error) {
        console.error(`API GET Error for ${url}:`, error);

        // Check if it's a network error
        if (axios.isAxiosError(error) && error.code === 'ERR_NETWORK') {
          retryCount++;
          if (retryCount <= maxRetries) {
            console.log(`Network error, retrying (${retryCount}/${maxRetries})...`);
            // Wait a bit before retrying
            await new Promise(resolve => setTimeout(resolve, 1000 * retryCount));
            return executeRequest();
          }

          // For metrics endpoints, return empty data instead of failing
          if (url.includes('/metrics')) {
            console.log('Returning empty data for metrics endpoint after network failure');
            return { data: [] as unknown as T };
          }
        }

        throw error;
      }
    };

    return executeRequest();
  },

  post: async <T = any>(url: string, data?: any, config?: any) => {
    try {
      return await axiosInstance.post<T>(url, data, config);
    } catch (error) {
      console.error(`API POST Error for ${url}:`, error);
      throw error;
    }
  },

  put: async (url: string, data: any, config?: any) => {
    try {
      return await axiosInstance.put(url, data, config);
    } catch (error) {
      console.error(`API PUT Error for ${url}:`, error);
      throw error;
    }
  },

  delete: async (url: string, config?: any) => {
    try {
      return await axiosInstance.delete(url, config);
    } catch (error) {
      console.error(`API DELETE Error for ${url}:`, error);
      throw error;
    }
  },

  // Domain-specific methods
  getHotels: async () => {
    try {
      const response = await axiosInstance.get('/hotels');
      return response.data;
    } catch (error) {
      console.error('Error fetching hotels:', error);
      return [];
    }
  },

  getHotelDetails: async (hotelId: string) => {
    try {
      const response = await axiosInstance.get(`/hotels/${hotelId}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching hotel ${hotelId}:`, error);
      throw error;
    }
  },

  getHotelDocuments: async (hotelId: string) => {
    try {
      const response = await axiosInstance.get(`/hotels/${hotelId}/documents`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching documents for hotel ${hotelId}:`, error);
      return [];
    }
  },

  // Analyze hotel data
  analyze: async (hotelInfo: HotelInfo, metrics: Metric[], hotelAiAnalysis?: any): Promise<AnalysisResponse> => {
    try {
      // Check for authentication token
      const token = tokenManager.getToken();
      console.log('⚠️ API analyze function called with token:', !!token, token ? `(length: ${token.length})` : 'missing');

      if (!token) {
        console.error('Authentication token missing');
        const error = new Error('Authentication required - please log in');
        (error as any).response = { status: 401 };
        throw error;
      }

      // Extract userId from token to include in request
      let userId: string | null = null;
      try {
        const parts = token.split('.');
        if (parts.length === 3) {
          const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
          userId = payload.userId || payload.sub;
          console.log('✅ Successfully extracted userId from token:', userId);
        }
      } catch (tokenError) {
        console.error('❌ Error extracting userId from token:', tokenError);
      }

      // Prepare hotel info for analysis (ensure arrays are handled if needed by backend)
      const formattedHotelInfo = {
        ...hotelInfo,
        // Keep amenities as an array if backend expects it directly
        amenities: Array.isArray(hotelInfo.amenities) ? hotelInfo.amenities : [],
        renovation_history: hotelInfo.renovation_history?.map((r) => ({
          cost: parseFloat(r.cost.toString()),
          year: parseInt(r.year.toString()),
          description: r.description?.trim() || '',
        })),
      };
      // Clean and validate metrics before sending
      const formattedMetrics = metrics
        .map((metric) => ({
          ...metric,
          value: cleanMetricValue(metric.value), // Use the imported utility function
          year: parseInt(metric.year.toString()), // Ensure year is a number
        }))
        .filter((m) => !isNaN(m.value) && !isNaN(m.year)); // Filter out invalid metrics

      console.log('API analyze: Creating request with token:', !!token);

      // Try with the user ID included in the payload as a workaround for missing authMiddleware
      return await retryWithBackoff(async () => {
        // First attempt: Send userId directly in the request body as a workaround
        const url = `/analysis/analyze`;
        console.log(`Making POST request to: ${url} with userId in payload as workaround`);

        try {
          // Include userId directly in the payload as a workaround for missing authMiddleware
          const response = await axiosInstance.post(url, {
            userId: userId, // Include userId in payload as workaround for missing authMiddleware
            hotelInfo: formattedHotelInfo,
            metrics: formattedMetrics,
            hotelAiAnalysis: hotelAiAnalysis
          });

          console.log('Analysis response received:', response.status, response.statusText);

          // Validate response structure
          if (!response?.data?.insights) {
            throw new Error('Invalid analysis response format');
          }
          return response.data;
        } catch (err: any) {
          console.error('Analysis request failed:', err.response?.status, err.response?.data);
          throw err;
        }
      });
    } catch (error: any) {
      console.error('❌ Analysis Error:', error);
      console.error('Error details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message
      });

      // Handle authentication errors
      if (error.response?.status === 401) {
        // Clear token if unauthorized
        tokenManager.removeToken();

        // Save current page URL for redirect back after login
        if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
          const currentPath = window.location.pathname;
          localStorage.setItem('loginRedirect', currentPath);
        }

        // Throw specific authentication error
        throw new Error('Authentication required - please log in again');
      }

      // Re-throw a more specific error or the original error
      throw error instanceof Error ? error : new Error('Analysis failed');
    }
  },

  // Update hotel details
  updateHotel: async (hotelId: string, hotelData: any) => {
    try {
      // Prepare data for update (ensure arrays are sent correctly)
      const formattedData = {
        ...hotelData,
        // Send amenities as a JavaScript array
        amenities: Array.isArray(hotelData.amenities) ? hotelData.amenities : [],
        // Format renovation history
        renovation_history: Array.isArray(hotelData.renovation_history)
          ? hotelData.renovation_history.map((r: any) => ({
              cost: parseFloat(r.cost?.toString() || '0'),
              year: parseInt(r.year?.toString() || new Date().getFullYear().toString()),
              description: r.description?.trim() || '',
            }))
          : [],
      };
      // Make the PUT request
      const response = await axiosInstance.put(`/hotels/${hotelId}`, formattedData);
      return response.data;
    } catch (error) {
      // Throw a more specific error
      throw error instanceof Error ? error : new Error('Failed to update hotel');
    }
  },

  // Save new hotel details
  saveHotelDetails: async (hotelData: any) => {
    try {
      // --- >> FIX APPLIED HERE << ---
      // Prepare data to send, ensuring 'amenities' is a JavaScript array
      const dataToSend = {
        ...hotelData,
        // Ensure amenities is always an array before sending
        amenities: Array.isArray(hotelData.amenities) ? [...hotelData.amenities] : [],
        // Apply similar logic if 'metrics' or 'categories' should also be arrays
        // metrics: Array.isArray(hotelData.metrics) ? [...hotelData.metrics] : [],
        // categories: Array.isArray(hotelData.categories) ? [...hotelData.categories] : [],
      };
      // --- >> END FIX << ---

      console.log('Data being sent to POST /hotels:', dataToSend); // Log data before sending

      // Make the POST request with the corrected data structure
      return await axiosInstance.post('/hotels', dataToSend);
    } catch (error: any) {
      // Log the specific error received from the backend if available
      console.error('Error saving hotel details:', error.response?.data || error.message);
      // Throw a more informative error message
      throw new Error(error.response?.data?.message || error.response?.data?.error || 'Failed to save hotel details');
    }
  },

  // Create a new deal
  createDeal: async (dealData: any) => {
    try {
      // Prepare payload for creating a deal
      const formattedPayload = {
        ...dealData,
        // Structure hotel data for the deal
        hotel_data: {
          name: dealData.hotelInfo.name,
          address: dealData.hotelInfo.address,
          city: dealData.hotelInfo.city,
          state: dealData.hotelInfo.state,
        },
        // Format financial metrics
        financial_metrics: dealData.metrics.map((m: any) => ({
          category: m.category.trim().toLowerCase(),
          value: Number(m.value), // Ensure value is a number
          year: m.year || new Date().getFullYear(), // Default year if missing
        })),
      };
      // Remove original metrics array if it exists (backend might expect only financial_metrics)
      if (formattedPayload.metrics) {
        delete formattedPayload.metrics;
      }
      // Make the POST request
      const response = await axiosInstance.post('/deals', formattedPayload);
      return response.data;
    } catch (error: any) {
      console.error('❌ Create Deal Error:', error);
      // Throw a more informative error
      throw new Error(error.response?.data?.error || error.message || 'Failed to create deal');
    }
  },

  // Get all deals
  getDeals: async () => {
    try {
      // Make the GET request
      const response = await axiosInstance.get('/deals');
      return response.data;
    } catch (error: any) {
      console.error('❌ Get Deals Error:', error);
      // Throw a more informative error
      throw new Error(error.message || 'Failed to fetch deals');
    }
  },

  // Delete a specific deal
  deleteDeal: async (dealId: string) => {
    try {
      console.log(`🗑️ Attempting to delete deal ${dealId}...`);
      // Retrieve authentication token
      const token = localStorage.getItem('token');
      console.log(`Token available for deletion: ${!!token}`);
      // Construct the full URL for the delete request
      const url = `${API_HOST}${API_PREFIX}/deals/${dealId}`;
      console.log(`Making DELETE request to: ${url}`);
      // Make the DELETE request using axios directly for clarity
      const response = await axios({
        method: 'DELETE',
        url: url, // Use the constructed URL
        headers: {
          'Content-Type': 'application/json',
          // Include Authorization header if token exists
          Authorization: token ? `Bearer ${token}` : '',
        },
        withCredentials: !isLocalDevelopment, // Include credentials based on environment
      });
      console.log('✅ Delete deal successful:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('❌ Delete Deal Error:', error);
      // Log detailed error information if available
      if (error.response) {
        console.error('Response status:', error.response.status);
        console.error('Response headers:', error.response.headers);
        console.error('Response data:', error.response.data);
      }
      // Throw a more specific error message
      throw new Error(error.response?.data?.error || error.message || 'Failed to delete deal');
    }
  },

  // Get market research data
  getMarketResearch: async (hotelId: string) => {
    try {
      // Make the GET request
      const response = await axiosInstance.get(`/market-research/${hotelId}/market-analysis`);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch market research:', error);
      throw error; // Re-throw the original error
    }
  },

  // Generate market research data
  generateMarketResearch: async (hotelId: string, params = {}) => {
    try {
      // Make the POST request with parameters
      const response = await axiosInstance.post(`/market-research/${hotelId}/market-analysis`, {
        marketParams: params,
      });
      return response.data;
    } catch (error) {
      console.error('Failed to generate market research:', error);
      throw error; // Re-throw the original error
    }
  },

  // Save a specific hotel metric (e.g., occupancy, ADR)
  saveHotelMetric: async (hotelId: string, type: string, year: number, value: number) => {
    try {
      // Make the POST request
      const response = await axiosInstance.post('/hotels/metrics', {
        hotelId,
        type, // The category of the metric (e.g., 'occupancy')
        year,
        value,
      });
      return response.data;
    } catch (error: any) {
      // Throw a more specific error message
      throw new Error(error.response?.data?.message || `Failed to save ${type} metric data`);
    }
  },

  // Get all metrics for a specific hotel
  getHotelMetrics: async (hotelId: string) => {
    try {
      // Ensure URL doesn't have duplicate /api prefix - this is a defensive measure
      const endpoint = `/hotels/${hotelId}/metrics`;

      // Make the GET request
      console.log(`Fetching metrics for hotel ${hotelId} with endpoint: ${endpoint}`);
      const response = await axiosInstance.get(endpoint);
      return response.data;
    } catch (error: any) {
      // Throw a more specific error message
      throw new Error(error.response?.data?.message || 'Failed to fetch hotel metrics');
    }
  },

  // Upload documents associated with a hotel
  uploadHotelDocuments: async (hotelId: string, files: File[], category?: string, description?: string) => {
    try {
      // Create FormData object for file upload
      const formData = new FormData();
      formData.append('hotelId', hotelId);
      // Append each file to the FormData
      files.forEach((file) => formData.append('files', file));
      // Append optional category and description
      if (category) formData.append('category', category);
      if (description) formData.append('description', description);

      // Make the POST request with multipart/form-data header
      const response = await axiosInstance.post('/uploads/hotels/documents', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error: any) {
      // Throw a more specific error message
      throw new Error(error.response?.data?.message || 'Failed to upload hotel documents');
    }
  },

  // Get URL for downloading a specific hotel document
  downloadHotelDocument: (documentId: string) => {
    // Construct the download URL
    return `${API_HOST}${API_PREFIX}/uploads/hotels/documents/${documentId}/download`;
  },

  // Delete a specific hotel document
  deleteHotelDocument: async (documentId: string) => {
    try {
      // Make the DELETE request
      const response = await axiosInstance.delete(`/uploads/hotels/documents/${documentId}`);
      return response.data;
    } catch (error: any) {
      // Throw a more specific error message
      throw new Error(error.response?.data?.message || 'Failed to delete hotel document');
    }
  },

  // Get all financial data for a specific hotel, trying multiple endpoints
  getHotelFinancialData: async (hotelId: string) => {
    try {
      console.log(`Fetching financial data for hotel ${hotelId}`);

      // First try the new comprehensive endpoint
      try {
        const comprehensiveData = await axiosInstance.get(`/hotels/${hotelId}/comprehensive-financials`);
        if (comprehensiveData.data && comprehensiveData.data.status === 'success') {
          console.log(`Found comprehensive financial data: ${comprehensiveData.data.summary.totalFinancialRecords} records`);
          return {
            source: 'comprehensive',
            data: comprehensiveData.data
          };
        }
      } catch (comprehensiveError) {
        console.warn('Comprehensive financials endpoint failed, trying individual endpoints');
      }

      // First try confirmed financials endpoint
      try {
        const confirmedData = await axiosInstance.get(`/hotels/${hotelId}/confirmed-financials`);
        if (confirmedData.data && confirmedData.data.length > 0) {
          console.log(`Found ${confirmedData.data.length} confirmed financial records`);
          return {
            source: 'confirmed-financials',
            data: confirmedData.data
          };
        }
      } catch (confirmedError) {
        console.warn('Confirmed financials endpoint failed, trying OCR data');
      }

      // Try OCR data if no confirmed financials
      try {
        const ocrData = await axiosInstance.get(`/ocr/hotel/${hotelId}/financials`);
        if (ocrData.data && ocrData.data.length > 0) {
          console.log(`Found ${ocrData.data.length} OCR financial records`);
          return {
            source: 'ocr',
            data: ocrData.data
          };
        }
      } catch (ocrError) {
        console.warn('OCR financials endpoint failed');
      }

      // Legacy attempt - try metrics endpoint as last resort
      try {
        const metricsData = await axiosInstance.get(`/hotels/${hotelId}/metrics`);
        if (metricsData.data && metricsData.data.length > 0) {
          console.log(`Found ${metricsData.data.length} metrics records`);
          return {
            source: 'metrics',
            data: metricsData.data
          };
        }
      } catch (metricsError) {
        console.warn('Metrics endpoint failed');
      }

      // If we reach here, no data was found
      console.log('No financial data found for hotel');
      return {
        source: null,
        data: []
      };
    } catch (error: any) {
      console.error('Error fetching hotel financial data:', error);
      throw new Error(error.response?.data?.message || 'Failed to fetch hotel financial data');
    }
  },

  // Get comprehensive financial data for a hotel (new method)
  getComprehensiveHotelData: async (hotelId: string) => {
    try {
      console.log(`Fetching comprehensive data for hotel ${hotelId}`);
      const response = await axiosInstance.get(`/hotels/${hotelId}/comprehensive-financials`);

      if (response.data && response.data.status === 'success') {
        console.log(`Comprehensive data retrieved: ${response.data.summary.totalFinancialRecords} financial records, ${response.data.summary.totalDocuments} documents`);
        return response.data;
      } else {
        throw new Error('Invalid response format from comprehensive financials endpoint');
      }
    } catch (error: any) {
      console.error('Error fetching comprehensive hotel data:', error);
      throw new Error(error.response?.data?.message || 'Failed to fetch comprehensive hotel data');
    }
  },

  // Sync financial data to ensure valuation analysis sees latest changes
  syncFinancialData: async (hotelId: string) => {
    try {
      console.log(`Syncing financial data for hotel ${hotelId}`);
      const response = await axiosInstance.post(`/hotels/${hotelId}/sync-financials`);

      if (response.data) {
        console.log(`✅ Financial sync completed: ${response.data.syncedRecords} records synchronized`);
        return response.data;
      } else {
        throw new Error('Invalid response format from sync endpoint');
      }
    } catch (error: any) {
      console.error('Error syncing financial data:', error);
      throw new Error(error.response?.data?.message || 'Failed to sync financial data');
    }
  },
};

// Export the API, token manager, and necessary utility functions
export default api;
export { axiosInstance }; // Export the original axios instance if needed
export const auth = tokenManager;